import React from 'react';
import { NextSeo, NextSeoProps } from 'next-seo';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ENVIRONMENT } from '@shared/utils/config/environment';
import { baseUrl } from '@/lib/misc/const';
interface SeoProps extends Partial<NextSeoProps> {
  date?: string;
}
const defaultMeta = {
  title: 'Genius | Instantly Buy & Sell Any Cryptocurrency | No KYC ',
  description: 'Trade thousands of tokens instantly with zero hassle - no KYC, token listings, or frozen funds. Sign up in 30 seconds. Buying tokens has never been easier with Genius.',
  siteName: 'Trade Crypto Like a Genius',
  type: 'website',
  image: 'https://uploads-ssl.webflow.com/66363bf0103ae3ba175412c9/6638e25c6c76db0df8e91daa_OG.jpeg'
};
const Seo: React.FC<SeoProps> = ({
  title,
  description,
  openGraph,
  twitter,
  date,
  noindex,
  nofollow,
  ...props
}) => {
  const router = useRouter();
  const currentPath = router.asPath;
  const currentUrl = `${baseUrl}${currentPath}`;
  const seoTitle = title ? title : defaultMeta.title;
  const seoDescription = description || defaultMeta.description;
  const seoNoindex = ENVIRONMENT === 'PROD' ? noindex : true;
  const seoNofollow = ENVIRONMENT === 'PROD' ? nofollow : true;
  const ogImage = openGraph?.images?.[0]?.url || defaultMeta.image;
  const seoProps: NextSeoProps = {
    title: seoTitle,
    description: seoDescription,
    canonical: currentUrl,
    nofollow: seoNofollow,
    noindex: seoNoindex,
    openGraph: {
      type: defaultMeta.type,
      locale: 'en_US',
      url: currentUrl,
      siteName: defaultMeta.siteName,
      title: seoTitle,
      description: seoDescription,
      defaultImageHeight: 1200,
      defaultImageWidth: 630,
      images: [{
        url: ogImage,
        width: 1200,
        height: 630,
        alt: seoTitle
      }],
      ...openGraph
    },
    twitter: {
      handle: '@GeniusTerminal',
      site: '@GeniusTerminal',
      cardType: 'summary_large_image',
      ...twitter
    },
    additionalMetaTags: [],
    ...props
  };
  if (date) {
    seoProps.additionalMetaTags?.push({
      name: 'article:published_time',
      content: date
    }, {
      name: 'publish_date',
      property: 'og:publish_date',
      content: date
    }, {
      name: 'author',
      property: 'article:author',
      content: 'Genius Trading'
    });
  }
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="Seo.tsx">
        <meta charSet='utf-8' data-sentry-element="meta" data-sentry-source-file="Seo.tsx" />
        <meta content='width=device-width, initial-scale=1' name='viewport' data-sentry-element="meta" data-sentry-source-file="Seo.tsx" />
        <meta name='twitter:image' content={ogImage} data-sentry-element="meta" data-sentry-source-file="Seo.tsx" />
        <link rel='icon' type='image/png' sizes='32x32' href='/static/images/webclip.png' />
        <link rel='icon' type='image/png' sizes='16x16' href='/static/images/webclip.png' />
        <link rel='apple-touch-icon' sizes='180x180' href='/static/images/webclip.png' />
        <link rel='mask-icon' href='/static/images/webclip.png' color='#5bbad5' />
        <meta name='msapplication-TileColor' content='#ffffff' data-sentry-element="meta" data-sentry-source-file="Seo.tsx" />
        <meta name='theme-color' content='#ffffff' data-sentry-element="meta" data-sentry-source-file="Seo.tsx" />
        <link rel='manifest' href='/manifest.json' />
        <meta name='application-name' content='Genius' data-sentry-element="meta" data-sentry-source-file="Seo.tsx" />
        <meta name='apple-mobile-web-app-capable' content='yes' data-sentry-element="meta" data-sentry-source-file="Seo.tsx" />
        <meta name='apple-mobile-web-app-status-bar-style' content='black-translucent' data-sentry-element="meta" data-sentry-source-file="Seo.tsx" />
        <meta name='apple-mobile-web-app-title' content='Genius' data-sentry-element="meta" data-sentry-source-file="Seo.tsx" />
        <meta name='mobile-web-app-capable' content='yes' data-sentry-element="meta" data-sentry-source-file="Seo.tsx" />
        <meta property='og:type' content='website' data-sentry-element="meta" data-sentry-source-file="Seo.tsx"></meta>
      </Head>
      <NextSeo {...seoProps} data-sentry-element="NextSeo" data-sentry-source-file="Seo.tsx" />
    </>;
};
export default Seo;